/**
 * Custom Terms Panel Component
 * 
 * Allows users to add and remove their own terms for each numerology number.
 * Terms are saved reliably to localStorage and persist across sessions.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Edit3, Save, Trash2, BookOpen } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  getCustomTerms,
  getCustomTermsForNumber,
  addCustomTerm,
  removeCustomTerm,
  clearCustomTermsForNumber,
  clearAllCustomTerms
} from '../utils/customTermsStorage';
import toast from 'react-hot-toast';

interface CustomTermsPanelProps {
  onClose?: () => void;
}

const NUMBERS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33, 44];

const CustomTermsPanel: React.FC<CustomTermsPanelProps> = ({ onClose }) => {
  const { colors } = useTheme();
  const [customTerms, setCustomTerms] = useState<Record<number, string[]>>({});
  const [editingNumber, setEditingNumber] = useState<number | null>(null);
  const [newTerm, setNewTerm] = useState('');
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);

  // Load custom terms on mount
  useEffect(() => {
    const terms = getCustomTerms();
    setCustomTerms(terms);
  }, []);

  // Handle adding a new term
  const handleAddTerm = (number: number) => {
    const term = newTerm.trim();
    if (!term) {
      toast.error('Please enter a term');
      return;
    }

    const success = addCustomTerm(number, term);
    if (success) {
      const updated = getCustomTerms();
      setCustomTerms(updated);
      setNewTerm('');
      setEditingNumber(null);
      toast.success(`Added "${term}" to number ${number}`);
    } else {
      // Check if it's a duplicate
      const existing = getCustomTermsForNumber(number);
      if (existing.some(t => t.toUpperCase() === term.toUpperCase())) {
        toast.error('This term already exists for this number');
      } else {
        toast.error('Failed to add term. Please try again.');
      }
    }
  };

  // Handle removing a term
  const handleRemoveTerm = (number: number, term: string) => {
    const success = removeCustomTerm(number, term);
    if (success) {
      const updated = getCustomTerms();
      setCustomTerms(updated);
      toast.success(`Removed "${term}" from number ${number}`);
    } else {
      toast.error('Failed to remove term. Please try again.');
    }
  };

  // Handle clearing all terms for a number
  const handleClearNumber = (number: number) => {
    if (window.confirm(`Remove all custom terms for number ${number}?`)) {
      const success = clearCustomTermsForNumber(number);
      if (success) {
        const updated = getCustomTerms();
        setCustomTerms(updated);
        toast.success(`Cleared all terms for number ${number}`);
      } else {
        toast.error('Failed to clear terms. Please try again.');
      }
    }
  };

  // Handle clearing all terms
  const handleClearAll = () => {
    if (window.confirm('Remove all custom terms for all numbers? This cannot be undone.')) {
      const success = clearAllCustomTerms();
      if (success) {
        setCustomTerms({});
        toast.success('Cleared all custom terms');
      } else {
        toast.error('Failed to clear terms. Please try again.');
      }
    }
  };

  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.95)',
      borderRadius: '16px',
      border: `2px solid ${colors.purple}`,
      padding: '1.5rem',
      maxWidth: '800px',
      maxHeight: '90vh',
      overflow: 'auto',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: `1px solid ${colors.purple}30`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <BookOpen size={24} color={colors.purple} />
          <h2 style={{
            color: colors.purple,
            fontSize: '1.5rem',
            fontWeight: '600',
            margin: 0
          }}>
            Custom Number Terms
          </h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.gray,
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${colors.purple}20`;
              e.currentTarget.style.color = colors.purple;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.gray;
            }}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Instructions */}
      <div style={{
        background: `rgba(139, 92, 246, 0.1)`,
        border: `1px solid ${colors.purple}30`,
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem',
        fontSize: '0.875rem',
        color: colors.light,
        lineHeight: '1.6'
      }}>
        <p style={{ margin: 0, marginBottom: '0.5rem' }}>
          Add your own personal terms to each numerology number. These terms will appear alongside the default archetype terms throughout the application.
        </p>
        <p style={{ margin: 0, fontSize: '0.75rem', color: colors.gray, fontStyle: 'italic' }}>
          Terms are saved automatically and will persist across sessions.
        </p>
      </div>

      {/* Number Selection Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        {NUMBERS.map(num => {
          const hasTerms = customTerms[num] && customTerms[num].length > 0;
          const isSelected = selectedNumber === num;
          
          return (
            <motion.button
              key={num}
              onClick={() => {
                setSelectedNumber(isSelected ? null : num);
                setEditingNumber(null);
                setNewTerm('');
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '1rem',
                background: isSelected 
                  ? `rgba(139, 92, 246, 0.3)`
                  : hasTerms
                    ? `rgba(139, 92, 246, 0.15)`
                    : 'rgba(100, 116, 139, 0.1)',
                border: `2px solid ${isSelected ? colors.purple : hasTerms ? `${colors.purple}50` : `${colors.gray}30`}`,
                borderRadius: '12px',
                color: isSelected ? colors.purple : colors.light,
                fontSize: '1.25rem',
                fontWeight: '700',
                cursor: 'pointer',
                fontFamily: 'monospace',
                position: 'relative',
                transition: 'all 0.2s ease'
              }}
            >
              {num}
              {hasTerms && (
                <span style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  background: colors.purple,
                  color: '#fff',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.65rem',
                  fontWeight: '600'
                }}>
                  {customTerms[num].length}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Selected Number Details */}
      <AnimatePresence>
        {selectedNumber !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              border: `1px solid ${colors.purple}30`,
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1rem',
              overflow: 'hidden'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                color: colors.purple,
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: 0,
                fontFamily: 'monospace'
              }}>
                Number {selectedNumber}
              </h3>
              <button
                onClick={() => handleClearNumber(selectedNumber)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${colors.rose}50`,
                  color: colors.rose,
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${colors.rose}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Trash2 size={14} />
                Clear All
              </button>
            </div>

            {/* Current Terms */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                color: colors.gray,
                fontSize: '0.75rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Custom Terms ({customTerms[selectedNumber]?.length || 0})
              </div>
              
              {customTerms[selectedNumber] && customTerms[selectedNumber].length > 0 ? (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {customTerms[selectedNumber].map((term, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: `rgba(139, 92, 246, 0.2)`,
                        border: `1px solid ${colors.purple}50`,
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        color: colors.light
                      }}
                    >
                      <span>{term}</span>
                      <button
                        onClick={() => handleRemoveTerm(selectedNumber, term)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.rose,
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${colors.rose}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                        title={`Remove "${term}"`}
                      >
                        <X size={14} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div style={{
                  color: colors.gray,
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                  padding: '1rem',
                  textAlign: 'center',
                  background: 'rgba(100, 116, 139, 0.1)',
                  borderRadius: '8px'
                }}>
                  No custom terms yet. Add your first term below.
                </div>
              )}
            </div>

            {/* Add New Term */}
            <div>
              <div style={{
                color: colors.gray,
                fontSize: '0.75rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Add New Term
              </div>
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTerm(selectedNumber);
                    }
                  }}
                  placeholder="Enter a term..."
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: `2px solid ${colors.purple}30`,
                    borderRadius: '8px',
                    color: colors.light,
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.purple;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = `${colors.purple}30`;
                  }}
                />
                <motion.button
                  onClick={() => handleAddTerm(selectedNumber)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={!newTerm.trim()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: newTerm.trim() ? colors.purple : `${colors.purple}30`,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: newTerm.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Plus size={16} />
                  Add
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clear All Button */}
      {Object.keys(customTerms).length > 0 && (
        <div style={{
          marginTop: '1.5rem',
          paddingTop: '1.5rem',
          borderTop: `1px solid ${colors.purple}30`,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={handleClearAll}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              border: `2px solid ${colors.rose}50`,
              borderRadius: '8px',
              color: colors.rose,
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${colors.rose}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <Trash2 size={16} />
            Clear All Custom Terms
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomTermsPanel;

